import React, { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db, auth } from '../firebase/config';
import { Lock } from 'lucide-react';

// 대표 잠금(PIN) — 2026-07-30 개편: 핀 검증은 서버(Cloud Function verifyOwnerPin)로 일원화
// 해제 상태는 MainLayout 대표 구역과 동일한 세션키(dcOwnerUnlocked) 공유 — 한 번 해제하면 전 구역 열림.
// 수동 [잠그기] 또는 브라우저/탭 종료 시 잠김 (타이머 없음 — 대표 결정).
// 구 sensitivePinHash(SHA-256) 사용자는 폴백으로 지원 (직원이 회사 핀을 쓰는 경우 포함).

const OWNER_KEY = 'dcOwnerUnlocked';
const LEGACY_KEY = 'dcSensitiveUnlockUntil';

export const sha256Hex = async (text) => {
    const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
    return Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, '0')).join('');
};

export const isUnlocked = () => sessionStorage.getItem(OWNER_KEY) === '1';
export const unlockSession = () => sessionStorage.setItem(OWNER_KEY, '1');
export const lockNow = () => { sessionStorage.removeItem(OWNER_KEY); sessionStorage.removeItem(LEGACY_KEY); };

function SensitiveGate({ user, children }) {
    const targetId = user?.companyId || user?.uid;
    const isSelf = targetId && targetId === auth.currentUser?.uid;
    const [state, setState] = useState('checking');   // checking | open | locked
    const [mode, setMode] = useState(null);           // 'server' | 'legacy'
    const [pinHash, setPinHash] = useState(null);
    const [pin, setPin] = useState('');
    const [err, setErr] = useState('');
    const [busy, setBusy] = useState(false);
    // 핀 분실 재설정 (2026-08-05): MainLayout 핀 모달과 동일한 이메일 인증코드 흐름 재사용
    const [resetMode, setResetMode] = useState(false);
    const [resetCode, setResetCode] = useState('');
    const [resetNewPin, setResetNewPin] = useState('');

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                if (!targetId) { if (alive) setState('open'); return; }
                if (isUnlocked()) { if (alive) setState('open'); return; }
                const snap = await getDoc(doc(db, 'users', targetId));
                const d = snap.exists() ? snap.data() : {};
                const hasNewPin = d.pinSet === true || !!d.pin;
                const hasLegacy = !!(d.sensitiveLockEnabled && d.sensitivePinHash);
                if (!hasNewPin && !hasLegacy) { if (alive) setState('open'); return; }
                if (alive) {
                    if (hasNewPin && isSelf) setMode('server');
                    else if (hasLegacy) { setMode('legacy'); setPinHash(d.sensitivePinHash); }
                    else setMode('server'); // 직원 계정 + 회사 신핀: 서버 검증은 본인 uid만 — 아래에서 안내
                    setState('locked');
                }
            } catch {
                // fail-closed: 조회 실패 시 잠금 유지 (핀 입력은 서버 검증이라 가능)
                if (alive) { setMode('server'); setState('locked'); }
            }
        })();
        return () => { alive = false; };
    }, [targetId, isSelf]);

    const tryUnlock = async () => {
        const value = pin.trim();
        if (!value || busy) return;
        setBusy(true);
        try {
            if (mode === 'legacy') {
                const h = await sha256Hex(value);
                if (h === pinHash) { unlockSession(); setErr(''); setPin(''); setState('open'); }
                else { setErr('PIN이 올바르지 않습니다.'); setPin(''); }
            } else {
                const fns = getFunctions(undefined, 'asia-northeast3');
                await httpsCallable(fns, 'verifyOwnerPin')({ pin: value });
                unlockSession(); setErr(''); setPin(''); setState('open');
            }
        } catch (e) {
            setErr(e?.message || 'PIN이 올바르지 않습니다.');
            setPin('');
        } finally {
            setBusy(false);
        }
    };

    // 핀 분실 — 가입 이메일로 인증코드 발송 (MainLayout startPinReset과 동일 CF)
    const startReset = async () => {
        if (busy) return;
        setBusy(true);
        setErr('');
        try {
            const fns = getFunctions(undefined, 'asia-northeast3');
            await httpsCallable(fns, 'requestPinResetCode')();
            setResetMode(true);
            setResetCode('');
            setResetNewPin('');
        } catch (e) {
            setErr(e?.message || '인증코드 발송에 실패했습니다.');
        } finally {
            setBusy(false);
        }
    };

    const submitReset = async () => {
        if (busy) return;
        setBusy(true);
        try {
            const fns = getFunctions(undefined, 'asia-northeast3');
            await httpsCallable(fns, 'resetPinWithCode')({ code: resetCode, newPin: resetNewPin });
            // 이메일 소유 증명 완료 = 본인 — 새 핀 설정과 동시에 해제 (MainLayout과 동일)
            unlockSession();
            setErr('');
            setResetMode(false);
            setState('open');
        } catch (e) {
            setErr(e?.message || '재설정에 실패했습니다.');
        } finally {
            setBusy(false);
        }
    };

    if (state === 'open') return children;
    if (state === 'checking') {
        return <div style={{ textAlign: 'center', padding: 80, color: '#94A3B8', fontSize: 14 }}>확인 중...</div>;
    }

    return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', padding: 20 }}>
            <div style={{ width: '100%', maxWidth: 360, background: '#fff', border: '1px solid #E2E8F0', borderRadius: 16, padding: '32px 28px', textAlign: 'center' }}>
                <div style={{ width: 52, height: 52, borderRadius: 14, background: '#EEF2FF', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
                    <Lock size={24} color="#6366F1" />
                </div>
                {resetMode ? (
                    <>
                        <div style={{ fontSize: 16, fontWeight: 800, color: '#0F172A', marginBottom: 4 }}>핀 재설정</div>
                        <div style={{ fontSize: 13, color: '#64748B', marginBottom: 18, lineHeight: 1.6 }}>
                            가입 이메일로 보낸 6자리 인증코드와<br />새 핀을 입력하세요 (15분 유효)
                        </div>
                        <input
                            type="text"
                            inputMode="numeric"
                            maxLength={6}
                            value={resetCode}
                            onChange={(e) => setResetCode(e.target.value.replace(/[^0-9]/g, ''))}
                            placeholder="이메일 인증코드 6자리"
                            autoFocus
                            style={{ width: '100%', boxSizing: 'border-box', padding: '12px 14px', fontSize: 16, textAlign: 'center', border: '1.5px solid #E2E8F0', borderRadius: 10, outline: 'none' }}
                        />
                        <input
                            type="password"
                            inputMode="numeric"
                            maxLength={6}
                            value={resetNewPin}
                            onChange={(e) => setResetNewPin(e.target.value.replace(/[^0-9]/g, ''))}
                            placeholder="새 핀 (4~6자리)"
                            style={{ width: '100%', boxSizing: 'border-box', marginTop: 8, padding: '12px 14px', fontSize: 16, textAlign: 'center', border: '1.5px solid #E2E8F0', borderRadius: 10, outline: 'none' }}
                        />
                        {err && <div style={{ color: '#DC2626', fontSize: 12.5, marginTop: 8 }}>{err}</div>}
                        <button
                            onClick={submitReset}
                            disabled={busy || resetCode.length !== 6 || resetNewPin.length < 4}
                            style={{ width: '100%', marginTop: 14, padding: '12px 0', background: (busy || resetCode.length !== 6 || resetNewPin.length < 4) ? '#A5B4FC' : '#6366F1', color: '#fff', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: busy ? 'default' : 'pointer' }}
                        >
                            {busy ? '확인 중...' : '새 핀으로 설정'}
                        </button>
                        <button
                            onClick={() => { setResetMode(false); setErr(''); }}
                            style={{ marginTop: 8, border: 'none', background: 'transparent', color: '#94A3B8', fontSize: 12.5, cursor: 'pointer' }}
                        >
                            {'← 핀 입력으로 돌아가기'}
                        </button>
                        <div style={{ fontSize: 11.5, color: '#94A3B8', marginTop: 12, lineHeight: 1.6 }}>
                            이메일 이용이 어려우면 010-2972-3733로 연락해주세요.
                        </div>
                    </>
                ) : (
                <>
                <div style={{ fontSize: 16, fontWeight: 800, color: '#0F172A', marginBottom: 4 }}>대표 메뉴 잠금</div>
                <div style={{ fontSize: 13, color: '#64748B', marginBottom: 18, lineHeight: 1.6 }}>
                    금액·정산 관련 화면입니다.<br />대표 PIN을 입력해주세요.
                </div>
                <input
                    type="password"
                    inputMode="numeric"
                    autoFocus
                    value={pin}
                    maxLength={6}
                    onChange={(e) => setPin(e.target.value.replace(/[^0-9]/g, ''))}
                    onKeyDown={(e) => { if (e.key === 'Enter') tryUnlock(); }}
                    placeholder="PIN 4~6자리"
                    style={{ width: '100%', boxSizing: 'border-box', padding: '12px 14px', fontSize: 18, letterSpacing: 8, textAlign: 'center', border: `1.5px solid ${err ? '#FCA5A5' : '#E2E8F0'}`, borderRadius: 10, outline: 'none' }}
                />
                {err && <div style={{ color: '#DC2626', fontSize: 12.5, marginTop: 8 }}>{err}</div>}
                <button
                    onClick={tryUnlock}
                    disabled={busy}
                    style={{ width: '100%', marginTop: 14, padding: '12px 0', background: busy ? '#A5B4FC' : '#6366F1', color: '#fff', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: busy ? 'default' : 'pointer' }}
                >
                    {busy ? '확인 중...' : '잠금 해제'}
                </button>
                {/* 핀 분실 안내 (2026-08-05): 재설정 진입 + 전화 안내 */}
                <button
                    onClick={startReset}
                    disabled={busy}
                    style={{ marginTop: 10, border: 'none', background: 'transparent', color: '#6366F1', fontSize: 12.5, cursor: busy ? 'default' : 'pointer', textDecoration: 'underline' }}
                >
                    핀을 잊으셨나요? 이메일로 재설정
                </button>
                <div style={{ fontSize: 11.5, color: '#94A3B8', marginTop: 8, lineHeight: 1.6 }}>
                    이메일 이용이 어려우면 010-2972-3733로 연락해주세요.
                </div>
                <div style={{ fontSize: 11.5, color: '#94A3B8', marginTop: 10, lineHeight: 1.6 }}>
                    한 번 해제하면 대표 메뉴 전체가 열리고,<br />브라우저를 닫거나 사이드메뉴 [잠그기]를 누르면 다시 잠깁니다.
                </div>
                </>
                )}
            </div>
        </div>
    );
}

export default SensitiveGate;
